import {
  buffers,
} from 'redux-saga'
import {
  all,
  take,
  put,
  select,
  cancel,
  cancelled,
  call,
  takeEvery,
  fork,
  actionChannel,
} from 'redux-saga/effects'
import T from 'i18n-react'

import {
  updateTablesAction,
  removeTables,
  clearTables,
} from 'containers/Entity/actions'
import {
  buildUrl,
} from 'helpers/actionHelper'
import {
  get,
  getRequest,
  post,
} from 'helpers/RequestUtil'
import {
  APPROVAL_PAGE_MOUNT,
  APPROVAL_PAGE_UNMOUNT,
  FETCH_APPROVAL_COUNT,
  FETCH_APPROVALS,
  FETCH_APPROVALS_SUCCESS,
  POST_APPROVAL_SKIP,
  POST_APPROVAL_REJECT,
  POST_APPROVAL_ACCEPT,
  CHANGE_PRODUCT_CATEGORY_FILTER,
  CHANGE_CURRENT_APPROVAL,
  CHANGE_CURRENT_APPROVAL_SUCCESS,
} from './constants'

import { APPROVE_TYPE } from 'containers/Approval/ApprovalDialog/constants'
import {
  WORKFLOW_COMMENT_TYPE,
} from 'containers/Approval/CommentSection/constants'
import {
  changeCurrentApprovalStart,
  changeCurrentApprovalSuccess,
  updateProductCategoryFilter,
  updateCurrentApproval,
  updateApprovalList,
  resetCurrentApproval,
  clearApprovalList,
  startFetchApprovals,
  fetchApprovalsStart,
  fetchApprovalsSuccess,
  fetchApprovalsError,
  postApprovalRejectStart,
  postApprovalRejectSuccess,
  postApprovalRejectError,
  postApprovalAcceptStart,
  postApprovalAcceptSuccess,
  postApprovalAcceptError,
  postApprovalSkipStart,
  postApprovalSkipSuccess,
  postApprovalSkipError,
  removeApproval,
  fetchApprovalCountStart,
  fetchApprovalCountSuccess,
  fetchApprovalCountError,
  updateApprovalCount,
} from './actions'
import {
  clearFormElements,
} from 'containers/Form/actions'
import {
  initRevisions,
} from 'containers/Project/Revision/actions'
import {
  productCategorySelector,
  currentApprovalSelector,
} from './selectors'
import {
  fetchApprovers,
  fetchApproversApprovals,
  watchFetchApproverImage,
} from 'containers/Approval/ApproverSection/sagas'
import {
  clearApprovers,
} from 'containers/Approval/ApproverSection/actions'
import {
  fetchComments,
  watchFetchComments,
  watchPostComments,
} from 'containers/Approval/CommentSection/sagas'
import {
  clearCommentList,
} from 'containers/Approval/CommentSection/actions'
import {
  resetSupportTeam,
} from 'containers/Project/SupportTeam/actions'
import {
  fetchHistory,
} from 'containers/Approval/HistorySection/sagas'
import {
  clearWorkflowHistory,
} from 'containers/Approval/HistorySection/actions'
import {
  fetchFormData,
} from 'containers/Approval/SpecSection/sagas'
import {
  fetchSchedule
} from 'containers/Approval/ScheduleSection/sagas'
import {
  handleFetchSupportTeam as supportTeam
 } from 'containers/Project/SupportTeam/sagas'
import {
  error,
  success
} from 'containers/App/Notification/actions'

const approvalsUrl = '/api/fulcrum/approvals'
const getApprovalUrl = (params) => buildUrl(approvalsUrl, ...params)

function* getApprovals(params) {
  try {
    yield put(startFetchApprovals())
    yield put(fetchApprovalsStart())
    const url = getApprovalUrl(params)
    const data = yield call(get, url)
    const {
      result: { approvals }
    } = data
    yield put(updateTablesAction(data))
    yield put(fetchApprovalsSuccess(approvals))
    return approvals
  } catch(error) {
    yield put(fetchApprovalsError(error))
  }
}

function* getApprovalCount() {
  try {
    yield put(fetchApprovalCountStart())
    const { data } = yield call(getRequest, '/api/fulcrum/approval_count')
    const approvalCount = data.attributes.approvalCount
    yield put(fetchApprovalCountSuccess())
    return approvalCount
  } catch(error) {
    yield put(fetchApprovalCountError(error))
  }
}

function* postApprovalReject(workflowId, id, comment) {
  try {
    yield put(postApprovalRejectStart())
    yield call(post, 'rejects', { workflowId, comment }, '/api/fulcrum/workflow/rejects')
    yield put(postApprovalRejectSuccess())
    yield put(success(T.translate('pages.project.approvals.notification.post_reject_success', {id})))
  } catch(error) {
    yield put(postApprovalRejectError(error))
  }
}

function* postApprovalAccept(workflowId, id) {
  try {
    yield put(postApprovalAcceptStart())
    yield call(post, 'accepts', { workflowId }, '/api/fulcrum/workflow/accepts')
    yield put(postApprovalAcceptSuccess())
    yield put(success(T.translate('pages.project.approvals.notification.post_accept_success', {id})))
  } catch(error) {
    yield put(postApprovalAcceptError(error))
  }
}

function* postApprovalSkip(workflowId, id, approvalType) {
  try {
    yield put(postApprovalSkipStart())
    var stateToSkipTo
    switch(approvalType) {
      case APPROVE_TYPE.SKIP_TO_KICKOFF.value:
        stateToSkipTo = 'kickoff'
        break
      case APPROVE_TYPE.SKIP_TO_DESIGN_REVIEW.value:
        stateToSkipTo = 'design_review'
        break
      case APPROVE_TYPE.SKIP_TO_QUALIFICATION.value:
        stateToSkipTo = 'qualification'
        break
      default:
        throw 'Invalid skip type.'
    } 
    yield call(post, 'skips', { workflowId, state: stateToSkipTo }, '/api/fulcrum/workflow/skips')
    yield put(postApprovalSkipSuccess())
    yield put(success(T.translate('pages.project.approvals.notification.post_accept_success', {id})))
  } catch(error) {
    yield put(postApprovalSkipError(error))
  }
}

function* approvalReject(action) {
  const { workflowId, approverWorkflowStateId, comment } = action
  yield call(postApprovalReject, workflowId, approverWorkflowStateId, comment)
  yield put(removeApproval(approverWorkflowStateId))
}

function* approvalAccept(action) {
  const { workflowId, approverWorkflowStateId } = action
  yield call(postApprovalAccept, workflowId, approverWorkflowStateId)
  yield put(removeApproval(approverWorkflowStateId))
}

function* approvalSkip(action) {
  const { workflowId, approverWorkflowStateId, approvalType } = action
  yield call(postApprovalSkip, workflowId, approverWorkflowStateId, approvalType)
  yield put(removeApproval(approverWorkflowStateId))
}

function* fetchApprovals() {
  const filter = yield select(productCategorySelector)
  const approvals = yield call(getApprovals, [{ type: 'project_type', value: filter }])
  yield put(updateApprovalList(approvals))
}

function* fetchApprovalCount() {
  const count = yield call(getApprovalCount)
  yield put(updateApprovalCount(count))
}

function* loadApproval(currentApproval) {
  const { workflowId, projectId } = currentApproval
  yield put(initRevisions())
  yield put(clearCommentList())
  yield put(clearApprovers())
  yield put(clearWorkflowHistory())
  yield put(clearFormElements())
  yield put(resetSupportTeam())
  yield put(removeTables('elements'))
  yield all([
    fork(fetchComments, workflowId, WORKFLOW_COMMENT_TYPE),
    fork(fetchApprovers, workflowId),
    fork(fetchApproversApprovals, workflowId),
    fork(fetchHistory, workflowId),
    fork(fetchFormData, projectId),
    fork(fetchSchedule, projectId),
    fork(supportTeam, { projectId })
  ])
}

function* changeFilter(action) {
  const { filter } = action
  yield put(updateProductCategoryFilter(filter))
  yield fork(fetchApprovals)
}

function* changeCurrentApproval(action) {
  const { approval } = action
  yield put(changeCurrentApprovalStart())
  yield put(updateCurrentApproval(approval))
  yield put(changeCurrentApprovalSuccess(approval))
}

function* watchFetchApprovals() {
  yield takeEvery(FETCH_APPROVALS, fetchApprovals)
}

function* watchFetchApprovalCount() {
  yield takeEvery(FETCH_APPROVAL_COUNT, fetchApprovalCount)
}

function* watchApprovalReject() {
  yield takeEvery(POST_APPROVAL_REJECT, approvalReject)
}

function* watchApprovalAccept() {
  yield takeEvery(POST_APPROVAL_ACCEPT, approvalAccept)
}

function* watchApprovalSkip() {
  yield takeEvery(POST_APPROVAL_SKIP, approvalSkip)
}

function* watchChangeCurrentApproval() {
  yield takeEvery(CHANGE_CURRENT_APPROVAL, changeCurrentApproval)
}

function* watchChangeProductCategoryFilter() {
  yield takeEvery(CHANGE_PRODUCT_CATEGORY_FILTER, changeFilter)
}

function* watchLoadApproval() {
  let task
  const loadApprovalChannel = yield actionChannel(
    CHANGE_CURRENT_APPROVAL_SUCCESS,
    buffers.sliding(1)
  )
  yield take(FETCH_APPROVALS_SUCCESS)

  while(true) {
    yield take(loadApprovalChannel)
    const currentApproval = yield select(currentApprovalSelector)

    // Important to getting next approvals
    // to clear the data from previous api calls
    if (task)
      yield cancel(task)

    if (currentApproval) {
      task = yield fork(loadApproval, currentApproval)
    }
  }
}

function* watchLoadApprovalPage() {
  while(true) {
    const task4 = yield fork(watchChangeCurrentApproval)
    yield take(APPROVAL_PAGE_MOUNT)
    const task = yield fork(watchLoadApproval)
    const task2 = yield fork(loadSagas)
    const task3 = yield fork(fetchApprovals)
    yield take(APPROVAL_PAGE_UNMOUNT)
    yield put(clearCommentList())
    yield put(clearApprovers())
    yield put(clearWorkflowHistory())
    yield put(resetCurrentApproval())
    yield put(clearApprovalList())
    yield put(resetSupportTeam())
    yield put(clearTables())
    yield cancel(task)
    yield cancel(task2)
    yield cancel(task3)
    yield cancel(task4)
  }
}

function* loadSagas() {
  yield all([
    fork(watchApprovalAccept),
    fork(watchApprovalReject),
    fork(watchApprovalSkip),
    fork(watchChangeProductCategoryFilter),
    fork(watchFetchApprovals),
    fork(watchFetchApproverImage),
    fork(watchFetchComments),
    fork(watchPostComments),
  ])
}

export {
  loadSagas,
  watchLoadApprovalPage,
  watchFetchApprovalCount,
}
